<script>
import AdminLayout from '@layouts/admin-main'
import AssetInfo from '@components/admin/asset-info'
import { fetchAssets, deleteAsset } from '@apis/admin/asset'
import PageNavigation from '@components/admin/page-navigation'

export default {
  page: {
    title: 'AssetList',
    meta: [{ name: 'description', content: 'AssetList' }],
  },
  components: { AdminLayout, AssetInfo, PageNavigation },
  data() {
    return {
      loading: false,
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      show_asset_info: false,
      target_asset_seq: 0,
      search_params: {},
    }
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadAssets().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const ret_obj = await fetchAssets({
        ...this.$route.query,
        no_paging: 0,
      })

      this.assets = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    handleCreateAsset() {
      this.show_asset_info = true
      this.target_asset_seq = null
    },
    handleUpdateAsset({ seq }) {
      this.show_asset_info = true
      this.target_asset_seq = seq
    },
    handleAssetSubmit(seq) {
      this.init()
      this.target_asset_seq = seq
    },
    async handleDeleteAsset(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteAsset(seq)
        await this.loadAssets()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = {}
      this.search()
    },
  },
}
</script>

<template>
  <AdminLayout v-loading.fullscreen="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['code', $t('asset_list.asset_code')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.is_active"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('asset_list.active') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.allow_deposit"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('asset_list.deposit') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.allow_withdraw"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('asset_list.withdrawala') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('asset_list.output_order')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.asc')],
                    ['desc', $t('admin.desc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>

          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('asset_list.asset_list') }}</h3>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('asset_list.output_order') }}</a></div
            ></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_decimal') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.is_active') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.allow_deposit') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.allow_withdraw') }}</div></th
          >
          <th scope="col">
            <div :class="$style.tRight">
              <a
                href="#"
                :class="[$style.button, $style.black]"
                @click.prevent="handleCreateAsset"
                ><span>{{ $t('admin.add') }}</span></a
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="asset in assets" :key="asset.seq">
          <td :class="$style.tCenter">{{ asset.list_order }}</td>
          <td :class="[$style.tCenter, $style.wide]">{{ asset.title }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ asset.code }}</td>
          <td :class="[$style.tRight, $style.nowrap]">{{
            asset.decimal_point
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            asset.is_active
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            asset.allow_deposit
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            asset.allow_withdraw
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              href="#"
              :class="[$style.button]"
              @click.prevent="handleUpdateAsset(asset)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
            {{ ' ' }}
            <a
              href="#"
              :class="[$style.button, $style.red]"
              @click.prevent="handleDeleteAsset(asset.seq)"
              ><span>{{ $t('admin.delete') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="!assets.length">
          <td :colspan="7" :class="$style.tCenter">{{
            $t('admin.no_asset')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog
      :title="
        target_asset_seq ? $t('admin.asset_modify') : $t('admin.asset_enroll')
      "
      :visible.sync="show_asset_info"
      width="1200px"
    >
      <AssetInfo
        v-if="show_asset_info"
        :key="target_asset_seq"
        :asset_seq="target_asset_seq"
        @submit="handleAssetSubmit"
      ></AssetInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
